/**
 * The grid's threshold filters, as one pure model.
 *
 * Sorting alone left the ranking dominated by negligible-volume rows — a creative that spent 2 units
 * and caught one order shows 567x ROAS and outranks everything the brand actually scaled. Bounds are
 * the other half of the sort, and the failure mode they introduce is a filter that is APPLIED but not
 * DISPLAYED: a grid showing a subset while reading as the whole set. So the popover inputs, the
 * active-filter chips, the URL parameters and the API request are all derived from the single
 * `CREATIVE_FILTERS` list below — "applied but invisible" is then unrepresentable, and a sixth filter
 * is one entry rather than four coordinated edits.
 *
 * Money is carried in MAJOR currency units (what the user typed and the grid displays), not micros:
 * the same numbers end up in a shareable browser URL, and `?minSpend=100000000` is a transcription
 * hazard. The API converts to micros once, at its own boundary.
 *
 * Pure — no React, no DOM, no clock.
 */

/** The bound fields, named exactly as the API query params so one list drives both. */
export interface ICreativeFilterState {
    minSpend?: number
    maxSpend?: number
    minRoas?: number
    maxRoas?: number
    minRevenue?: number
    maxRevenue?: number
    minCpa?: number
    maxCpa?: number
    minImpressions?: number
    maxImpressions?: number
}

export type ICreativeFilterField = keyof ICreativeFilterState

/**
 * How every filter control writes a bound: as a function of the CURRENT filters, never a precomputed
 * object. The rendered state is the URL, which only refreshes after `router.replace` round-trips, so
 * two writes fired inside that window (a metric's min and max, two quick presets) must each merge
 * against the freshest state at fire time rather than clobber one another. See the lost-update tests.
 */
export type ICreativeFilterUpdate = (current: ICreativeFilterState) => ICreativeFilterState

/** How a bound's value is written and read: currency, a ROAS multiple, or a plain count. */
export type ICreativeFilterUnit = 'money' | 'ratio' | 'count'

export interface ICreativeFilterDef {
    key: 'spend' | 'roas' | 'revenue' | 'cpa' | 'impressions'
    label: string
    unit: ICreativeFilterUnit
    minField: ICreativeFilterField
    maxField: ICreativeFilterField
    /** `step` for the numeric inputs — whole units for counts, fractions for ratios and money. */
    step: string
    placeholderMin: string
    placeholderMax: string
}

/**
 * The bounded metrics, in the order they appear in the popover. A subset of the metrics the grid
 * ranks by; anything sortable but unbounded would leave the distorted-ranking problem unfixable for
 * that metric. `costPerResult` is deliberately absent — it compares a lead against a ThruPlay against
 * a purchase, so one bound cannot mean the same thing on two rows of the same grid.
 */
export const CREATIVE_FILTERS: ICreativeFilterDef[] = [
    {
        key: 'spend',
        label: 'Spend',
        unit: 'money',
        minField: 'minSpend',
        maxField: 'maxSpend',
        step: '1',
        placeholderMin: 'Min',
        placeholderMax: 'Max',
    },
    {
        key: 'roas',
        label: 'ROAS',
        unit: 'ratio',
        minField: 'minRoas',
        maxField: 'maxRoas',
        step: '0.1',
        placeholderMin: '2.0',
        placeholderMax: 'Max',
    },
    {
        key: 'revenue',
        label: 'Purchase value',
        unit: 'money',
        minField: 'minRevenue',
        maxField: 'maxRevenue',
        step: '1',
        placeholderMin: 'Min',
        placeholderMax: 'Max',
    },
    {
        key: 'cpa',
        label: 'Cost / purchase',
        unit: 'money',
        minField: 'minCpa',
        maxField: 'maxCpa',
        step: '1',
        placeholderMin: 'Min',
        placeholderMax: 'Max',
    },
    {
        key: 'impressions',
        label: 'Impressions',
        unit: 'count',
        minField: 'minImpressions',
        maxField: 'maxImpressions',
        step: '100',
        placeholderMin: '1000',
        placeholderMax: 'Max',
    },
]

/** Every bound field, in definition order — the list the URL parser and the request builder walk. */
export const CREATIVE_FILTER_FIELDS: ICreativeFilterField[] = CREATIVE_FILTERS.flatMap((f) => [f.minField, f.maxField])

/** The unfiltered state. Shared so "no filters" is one value, not a literal repeated per call site. */
export const EMPTY_CREATIVE_FILTERS: ICreativeFilterState = {}

/**
 * One-click minimum-spend floors, in the account's own currency units. Deliberately round rather than
 * data-derived: a threshold computed from the visible set would move under the user every time the
 * range changed, and the point of the control is a stable, obvious "ignore the noise" action.
 */
export const MIN_SPEND_PRESETS = [50, 100, 500, 1000]

/** How many bounds are actually set — the count the `Filters` trigger shows. */
export function activeFilterCount(state: ICreativeFilterState): number {
    return CREATIVE_FILTER_FIELDS.filter((field) => state[field] !== undefined).length
}

export function hasActiveFilters(state: ICreativeFilterState): boolean {
    return activeFilterCount(state) > 0
}

/**
 * Narrow an arbitrary value to a usable bound, or `undefined`. Empty, negative, non-finite and
 * unparseable all read as "unset" rather than `0` — `Number('')` is `0`, and a maximum of 0 would
 * silently empty the grid.
 */
export function parseFilterValue(raw: string | number | null | undefined): number | undefined {
    if (raw === null || raw === undefined) return undefined
    const trimmed = typeof raw === 'number' ? raw : raw.trim()
    if (trimmed === '') return undefined
    const value = Number(trimmed)
    return Number.isFinite(value) && value >= 0 ? value : undefined
}

/** Set or clear one bound, returning a new state. An unusable input clears rather than storing junk. */
export function setFilterValue(
    state: ICreativeFilterState,
    field: ICreativeFilterField,
    raw: string | number | null | undefined
): ICreativeFilterState {
    const next = { ...state }
    const value = parseFilterValue(raw)
    if (value === undefined) delete next[field]
    else next[field] = value
    return next
}

/** Remove one bound — the per-chip dismissal. */
export function clearFilterField(state: ICreativeFilterState, field: ICreativeFilterField): ICreativeFilterState {
    const next = { ...state }
    delete next[field]
    return next
}

/** Remove several bounds at once — a metric pill dismisses both of its bounds together. */
export function clearFilterFields(state: ICreativeFilterState, fields: ICreativeFilterField[]): ICreativeFilterState {
    return fields.reduce((acc, field) => clearFilterField(acc, field), state)
}

/** Money in major units → a readable, exact string. Falls back when the account currency is unknown. */
function formatMoney(major: number, currency: string): string {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: Number.isInteger(major) ? 0 : 2,
        }).format(major)
    } catch {
        // No/unknown currency — show the bare number rather than a wrong symbol.
        return currency ? `${major.toLocaleString()} ${currency}` : major.toLocaleString()
    }
}

/** A bound's value as the user should read it back: currency, a ROAS multiple, or a grouped count. */
export function formatFilterValue(unit: ICreativeFilterUnit, value: number, currency: string): string {
    if (unit === 'money') return formatMoney(value, currency)
    if (unit === 'ratio') return `${value}x`
    return value.toLocaleString()
}

export interface IActiveFilterPill {
    key: ICreativeFilterDef['key']
    /** The metric name — the pill's fixed label (e.g. "ROAS"). */
    label: string
    /** The bound as one readable value: `≥ x`, `≤ y`, or `x – y`. */
    value: string
    /** The fields this pill's dismissal clears — one for a single bound, both for a range. */
    removeFields: ICreativeFilterField[]
}

/**
 * The applied bounds as one pill PER bounded metric (Linear-style), collapsing a metric's min and max
 * into a single range value. Distinct from `activeFilterChips`, which is one chip per bound; the pill
 * reads "ROAS 2x – 5x" rather than two separate "ROAS ≥ 2x" / "ROAS ≤ 5x" chips.
 */
export function activeFilterPills(state: ICreativeFilterState, currency: string): IActiveFilterPill[] {
    const pills: IActiveFilterPill[] = []
    for (const def of CREATIVE_FILTERS) {
        const min = state[def.minField]
        const max = state[def.maxField]
        if (min === undefined && max === undefined) continue

        const format = (value: number) => formatFilterValue(def.unit, value, currency)
        const removeFields: ICreativeFilterField[] = []
        if (min !== undefined) removeFields.push(def.minField)
        if (max !== undefined) removeFields.push(def.maxField)

        const value =
            min !== undefined && max !== undefined
                ? `${format(min)} – ${format(max)}`
                : min !== undefined
                  ? `≥ ${format(min)}`
                  : `≤ ${format(max as number)}`

        pills.push({ key: def.key, label: def.label, value, removeFields })
    }
    return pills
}
